import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Radio,
  Tooltip,
} from '@mui/material';
import { ArrowBack, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { FiExternalLink, FiX, FiSend, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { Search } from '@mui/icons-material';
import { whatsappTemplatesAPI, contactsAPI } from '../services/api';
import { showError, showSuccess } from '../utils/toast';
import { colors } from '../theme/modernTheme';
import WhatsAppTemplatePreview from '../components/WhatsAppTemplatePreview';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import WhatsAppTemplateDeleteContent from '../components/WhatsAppTemplateDeleteContent';

export default function WhatsAppTemplateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [wabaId, setWabaId] = useState('');
  
  // Send Test Message states
  const [sendTestDialogOpen, setSendTestDialogOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [contactsPage, setContactsPage] = useState(1);
  const contactsPerPage = 10;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await whatsappTemplatesAPI.getById(id);
      setTemplate(response.data?.template);
      setBusinessId(response.data?.business_id || '');
      setWabaId(response.data?.waba_id || '');
    } catch (err) {
      console.error('Error loading WhatsApp template:', err);
      const errorMessage = err.response?.data?.error?.message 
        || err.response?.data?.message 
        || 'Failed to load template';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleBack = () => {
    navigate('/whatsapp-templates');
  };

  // Load contacts for send test
  const loadContacts = async () => {
    try {
      setLoadingContacts(true);
      const response = await contactsAPI.getAll({ 
        page: 1, 
        limit: 1000,
        fields: 'id,type,first_name,last_name,organization_name,primary_email,phone_1' 
      });
      const contactsWithPhone = response.data.filter(c => c.phone_1);
      setContacts(contactsWithPhone);
      setFilteredContacts(contactsWithPhone);
    } catch (err) {
      console.error('Error loading contacts:', err);
      showError('Failed to load contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  // Handle send test dialog open
  const handleOpenSendTest = () => {
    setSendTestDialogOpen(true);
    if (contacts.length === 0) {
      loadContacts();
    }
  };

  // Handle send test dialog close
  const handleCloseSendTest = () => {
    if (!sendingTest) {
      setSendTestDialogOpen(false);
      setSelectedContactId(null);
      setContactSearchTerm('');
      setContactsPage(1);
      setFilteredContacts(contacts);
    }
  };

  // Handle contact search
  const handleContactSearch = (searchValue) => {
    setContactSearchTerm(searchValue);
    setContactsPage(1); // Reset to first page on search
    if (!searchValue.trim()) {
      setFilteredContacts(contacts);
      return;
    }
    const searchStr = searchValue.toLowerCase();
    const filtered = contacts.filter(contact => {
      const name = contact.type === 'INDIVIDUAL'
        ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim().toLowerCase()
        : (contact.organization_name || '').toLowerCase();
      const email = (contact.primary_email || '').toLowerCase();
      const phone = (contact.phone_1 || '').toLowerCase();
      return name.includes(searchStr) || email.includes(searchStr) || phone.includes(searchStr);
    });
    setFilteredContacts(filtered);
  };

  // Handle send test message
  const handleSendTestMessage = async () => {
    if (!selectedContactId) {
      showError('Please select a contact');
      return;
    }

    try {
      setSendingTest(true);
      await whatsappTemplatesAPI.sendTestMessage({
        template_id: id,
        contact_id: selectedContactId
      });
      showSuccess('Test message sent successfully!');
      // Delay closing the dialog to allow toast to display
      setTimeout(() => {
        handleCloseSendTest();
      }, 500);
    } catch (err) {
      console.error('Error sending test message:', err);
      const errorMessage = err.response?.data?.error?.message 
        || err.response?.data?.message 
        || 'Failed to send test message';
      showError(errorMessage);
    } finally {
      setSendingTest(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!template) return;

    setDeleting(true);
    try {
      await whatsappTemplatesAPI.delete(template.id, template.name);
      showSuccess('Template deleted successfully');
      setDeleteDialogOpen(false);
      // Navigate back to templates list after successful deletion
      navigate('/whatsapp-templates');
    } catch (error) {
      showError(error.message || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // if (error || !template) {
  //   return (
  //     <Box sx={{ p: 3 }}>
  //       <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBack />}>
  //         Back to Templates
  //       </Button>
  //     </Box>
  //   );
  // }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        p: { xs: 1, sm: 2, md: 2 },
      }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{
              '&:hover': { color: 'rgba(0, 0, 0, 1)' },
            }}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: '#000000', fontSize: '1.25rem' }}>
              {template.name}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Send Test" arrow>
            <span>
              <IconButton
                onClick={handleOpenSendTest}
                disabled={template?.status !== 'APPROVED'}
                sx={{
                  color: colors.success.main,
                  '&:hover': {
                    backgroundColor: `${colors.success.main}15`,
                  },
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                }}>
                <FiSend size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit Template" arrow>
            <IconButton
              onClick={() => navigate(`/whatsapp-templates/edit/${id}`)}
              sx={{
                color: colors.secondary.main,
                '&:hover': {
                  backgroundColor: `${colors.secondary.main}15`,
                },
              }}>
              <FiEdit2 size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip 
            title={
              template?.can_delete === false
                ? "Cannot delete - used in active campaigns"
                : "Delete"
            } 
            arrow>
            <span>
              <IconButton
                onClick={handleDeleteClick}
                disabled={template?.can_delete === false}
                sx={{
                  color: colors.error.main,
                  '&:hover': {
                    backgroundColor: `${colors.error.main}15`,
                  },
                  '&:disabled': {
                    opacity: 0.4,
                    cursor: 'not-allowed',
                  },
                }}>
                <FiTrash2 size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Manage Template" arrow>
            <span>
              <IconButton
                onClick={() => window.open(`https://business.facebook.com/latest/whatsapp_manager/message_templates?business_id=${businessId}&asset_id=${wabaId}&id=${template.id}&tab=template-details`, '_blank')}
                disabled={!businessId || !wabaId}
                sx={{
                  color: colors.primary.main,
                  '&:hover': {
                    backgroundColor: `${colors.primary.main}15`,
                  },
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                }}>
                <FiExternalLink size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Content Section */}
      <Card
        sx={{
          p: 3,
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
        <WhatsAppTemplatePreview template={template} />
      </Card>

      {/* Send Test Message Dialog */}
      <Dialog
        open={sendTestDialogOpen}
        onClose={handleCloseSendTest}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '4px',
          },
        }}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}>
          Send Test Message
          <IconButton
            onClick={handleCloseSendTest}
            disabled={sendingTest}
            size="small"
            sx={{
              color: colors.text.secondary,
              '&:hover': {
                color: colors.text.primary,
                backgroundColor: colors.action.hover,
              },
            }}>
            <FiX size={20} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
            Select a contact with a valid phone number to receive the test message.
          </Alert>

          {/* Search contacts */}
          <TextField
            fullWidth
            placeholder="Search by name, email, or phone number..."
            size="small"
            value={contactSearchTerm}
            onChange={(e) => handleContactSearch(e.target.value)}
            disabled={sendingTest}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.background.default,
              },
            }}
          />

          {/* Contacts table */}
          {loadingContacts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredContacts.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {contactSearchTerm 
                  ? `No contacts found matching "${contactSearchTerm}"` 
                  : 'No contacts with phone numbers found'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  border: `1px solid ${colors.border.main}`,
                  maxHeight: '400px',
                  overflow: 'auto',
                }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: '5%' }}></TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Phone</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredContacts
                      .slice(
                        (contactsPage - 1) * contactsPerPage,
                        contactsPage * contactsPerPage
                      )
                      .map((contact) => (
                        <TableRow
                          key={contact.id}
                          hover
                          onClick={() => setSelectedContactId(contact.id)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: colors.action.hover,
                            },
                          }}>
                          <TableCell padding="checkbox">
                            <Radio
                              checked={selectedContactId === contact.id}
                              onChange={() => setSelectedContactId(contact.id)}
                              disabled={sendingTest}
                              sx={{
                                '&.Mui-checked': {
                                  color: colors.primary.main,
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {contact.type === 'INDIVIDUAL'
                              ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed Contact'
                              : contact.organization_name || 'Unnamed Organization'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {contact.phone_1 || '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={contact.type}
                              size="small"
                              color={contact.type === 'INDIVIDUAL' ? 'primary' : 'secondary'}
                              sx={{
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {filteredContacts.length > contactsPerPage && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={2}
                  mt={2}>
                  <IconButton
                    size="small"
                    onClick={() => setContactsPage(Math.max(1, contactsPage - 1))}
                    disabled={contactsPage === 1}
                    sx={{
                      '&:hover': {
                        backgroundColor: colors.action.hover,
                      },
                      '&:disabled': {
                        opacity: 0.5,
                      },
                    }}>
                    <ChevronLeft />
                  </IconButton>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      minWidth: '100px',
                      textAlign: 'center',
                      fontSize: '0.875rem',
                    }}>
                    Page {contactsPage} of {Math.ceil(filteredContacts.length / contactsPerPage)}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setContactsPage(
                        Math.min(
                          Math.ceil(filteredContacts.length / contactsPerPage),
                          contactsPage + 1
                        )
                      )
                    }
                    disabled={contactsPage >= Math.ceil(filteredContacts.length / contactsPerPage)}
                    sx={{
                      '&:hover': {
                        backgroundColor: colors.action.hover,
                      },
                      '&:disabled': {
                        opacity: 0.5,
                      },
                    }}>
                    <ChevronRight />
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseSendTest}
            disabled={sendingTest}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '4px',
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendTestMessage}
            disabled={!selectedContactId || sendingTest}
            startIcon={sendingTest ? <CircularProgress size={16} /> : <FiSend />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '4px',
              backgroundColor: colors.success.main,
              '&:hover': {
                backgroundColor: colors.success.dark,
              },
            }}>
            {sendingTest ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="template"
        deleting={deleting}
        maxWidth="520px">
        <WhatsAppTemplateDeleteContent />
      </DeleteConfirmationDialog>
    </Box>
  );
}
